import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { AuthorCollectingSocietyUpdateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { CollectiveManagementSocietyCreate } from "../../../collectiveManagementSociety/components/create/CollectiveManagementSocietyCreate";
import { CollectiveManagementSocietyCreateStore } from "../../../collectiveManagementSociety/store";
import { InputString } from "../../../common/controls";
import { InputReferenceSet } from "../../../common/controls";
import { TerritoryCreate } from "../../../territory/components/create/TerritoryCreate";
import { TerritoryCreateStore } from "../../../territory/store";
import { FormControl } from "@mui/material";

export type AuthorCollectingSocietyUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  collectingSocietySelectedValue?: { displayId: string; id: string } | null;
  onCollectingSocietyChange?: (value: string) => void;
  onCollectingSocietySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  collectingSocietyOptions?: { displayId: string; id: string }[];
  collectingSocietyError?: string;
  memberNumber: string;
  memberNumberSelectedValue?: { displayId: string; id: string } | null;
  onMemberNumberChange?: (value: string) => void;
  memberNumberError?: string;
  territoriesSelectedValues: { displayId: string; id: string }[];
  onTerritoriesChange?: (value: string) => void;
  onTerritoriesSelected?: (value: { displayId: string; id: string }[]) => void;
  territoriesOptions?: { displayId: string; id: string }[];
  territoriesError?: string;
  authorCollectingSocietyUpdateStore: AuthorCollectingSocietyUpdateStore;
};

export const AuthorCollectingSocietyUpdateForm: React.FC<AuthorCollectingSocietyUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onCollectingSocietySelected = () => {},
      collectingSocietyOptions = [],
      collectingSocietySelectedValue = null,
      collectingSocietyError,
      onCollectingSocietyChange = () => {},
      memberNumber = "",
      memberNumberError,
      onMemberNumberChange = () => {},
      onTerritoriesSelected = () => {},
      territoriesOptions = [],
      territoriesSelectedValues,
      territoriesError,
      onTerritoriesChange = () => {},
      authorCollectingSocietyUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="AuthorCollectingSociety"
              mode="update"
              values={{}}
              options={{ collectingSociety: collectingSocietyOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "collectingSociety":
                    onCollectingSocietySelected?.(valor);
                    break;
                  case "memberNumber":
                    onMemberNumberChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={authorCollectingSocietyUpdateStore.getCardErrors(
                "null"
              )}
              title={langStore.groupAuthorCollectingSocietyLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="collectingSociety"
                  label={
                    langStore.AuthorCollectingSocietyCollectingSocietyLabel
                  }
                  autoFocus
                  error={Boolean(collectingSocietyError)}
                  helperText={
                    collectingSocietyError ||
                    authorCollectingSocietyUpdateStore.warnings[
                      "collectingSociety"
                    ]?.message
                  }
                  selectedValue={collectingSocietySelectedValue}
                  onChange={(value: string) => onCollectingSocietyChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onCollectingSocietySelected(value)
                  }
                  options={collectingSocietyOptions}
                  description={
                    langStore.AuthorCollectingSocietyCollectingSocietyDescription
                  }
                  canCreate={rootStore.authStore.hasPermissions(
                    "collectiveManagementSociety_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new CollectiveManagementSocietyCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <CollectiveManagementSocietyCreate
                          collectiveManagementSocietyCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewCollectiveManagementSociety"
                      cancelMethod="cancelNewCollectiveManagementSociety"
                    />
                  )}
                />
                <InputString
                  value={memberNumber}
                  error={Boolean(memberNumberError)}
                  warning={
                    !memberNumberError &&
                    Boolean(
                      authorCollectingSocietyUpdateStore.warnings[
                        "memberNumber"
                      ]
                    )
                  }
                  helperText={
                    memberNumberError ||
                    authorCollectingSocietyUpdateStore.warnings["memberNumber"]
                      ?.message
                  }
                  label={langStore.AuthorCollectingSocietyMemberNumberLabel}
                  onChange={(value: string) => onMemberNumberChange(value)}
                  description={
                    langStore.AuthorCollectingSocietyMemberNumberDescription
                  }
                />
                <InputReferenceSet
                  id="territories"
                  label={langStore.AuthorCollectingSocietyTerritoriesLabel}
                  error={Boolean(territoriesError)}
                  helperText={
                    territoriesError ||
                    authorCollectingSocietyUpdateStore.warnings["territories"]
                      ?.message
                  }
                  selectedValues={territoriesSelectedValues}
                  onChange={(value: string) => onTerritoriesChange(value)}
                  onSelect={(value: { displayId: string; id: string }[]) =>
                    onTerritoriesSelected(value)
                  }
                  options={territoriesOptions}
                  description={
                    langStore.AuthorCollectingSocietyTerritoriesDescription
                  }
                  pinnedDisplayIds={["Todo el mundo"]}
                  canCreate={rootStore.authStore.hasPermissions(
                    "territory_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new TerritoryCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <TerritoryCreate
                          territoryCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewTerritory"
                      cancelMethod="cancelNewTerritory"
                    />
                  )}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
